// ═══════════════════════════════════════════════════════════════════════════
// PAGES — PLANS D'ACTION (liste + page de mise à jour)
// Juin 2026. Lecture gardée par action_plans.read, écriture par action_plans.write.
// ═══════════════════════════════════════════════════════════════════════════
//
// Deux pages dans ce module :
//   - pages.actionPlans       → #action-plans : liste triable des plans.
//   - pages.actionPlan        → #action-plan/:id : formulaire du plan + arbre
//                               des phases/volets (UX reprise de #admin/structures :
//                               arbre dépliable + drag&drop de prédécesseurs +
//                               modale avec cases à cocher) + actions par phase.
//
// La logique pure du graphe (children/descendants/relink) est réutilisée depuis
// domain/structure-graph.ts en mappant predecessor_ids → parent_ids. Les classes
// CSS `struct-*` sont réutilisées pour rester visuellement aligné sur les
// structures.

import {
  childrenMap as graphChildrenMap,
  computeRelinkParents,
  descendantIds as graphDescendantIds,
} from '../domain/structure-graph.js';
import { sortActions as domainSortActions } from '../domain/actions-sort.js';
import { clearDropHints, createTreeDndHandlers, rowFromEvent } from '../utils/tree-dnd.js';
import {
  sortPlans as domainSortPlans,
  dropTargetStatus as domainDropTargetStatus,
  type DropStatus,
  relinkChangesNothing,
  filterCompletedActions,
  phaseDeadlineAlert as domainPhaseDeadlineAlert,
  smartCriteriaValid,
} from '../domain/action-plan.js';
import { navigateTo } from '../router.js';
import { openExportMenu } from '../utils/export-menu.js';
import { confirmDelete } from '../utils/confirm-delete.js';
// Page KPI d'une phase + helpers santé (pastilles) extraits dans
// page-action-plans-kpi.ts (découpe god-module 2026-07). Cet import déclenche
// l'enregistrement de `pages.actionPlanPhaseKpi` (effet de bord) et fournit
// `phaseKpiPastilles` + le type `PhaseHealth` à l'arbre des phases ci-dessous.
import { phaseKpiPastilles, type PhaseHealth } from './page-action-plans-kpi.js';

interface PlanRow {
  id: number;
  code: string;
  title: string;
  description: string | null;
  date_start: string | null;
  date_end: string | null;
  is_active: boolean;
  phase_count?: number;
  avancement: number | null;
  meteo: string | null;
}

// Action telle que rendue par le composant de ligne partagé (componentActionRow,
// même format que #revision). On réutilise sa forme de paramètre pour rester
// aligné sans dupliquer le contrat.
type RowAction = Parameters<typeof window.componentActionRow.buildActionRowHtml>[0];
type RowOpts = NonNullable<Parameters<typeof window.componentActionRow.buildActionRowHtml>[1]>;

interface PhaseT {
  id: number;
  plan_id: number;
  code: string;
  title: string;
  description: string | null;
  date_start: string | null;
  date_end_target: string | null;
  predecessor_ids: number[];
  avancement: number | null;
  action_count?: number;
  actions?: RowAction[];
  // Phase Intelligente : drapeau + critères (id + code pour l'affichage).
  is_smart?: boolean;
  smart_program_id?: number | null;
  smart_program_code?: string | null;
  smart_project_id?: number | null;
  smart_project_code?: string | null;
  smart_structure_id?: number | null;
  smart_structure_code?: string | null;
  smart_committee_id?: number | null;
  smart_committee_code?: string | null;
  // Santé KPI (4 couleurs) — renseignée par le GET détail du plan.
  health?: PhaseHealth;
}

interface PlanDetail extends PlanRow {
  phases: PhaseT[];
}

interface MinUser {
  id: number;
  name?: string;
  trigram?: string | null;
  is_team?: boolean;
}

interface MinStatus {
  id: number;
  position: number;
  name?: string;
  is_final?: boolean;
}

interface MinProject {
  id: number;
  name?: string;
  code?: string | null;
}

// Options de l'éditeur d'action (typées par component-action-modal via window).
// On reste structurel : nos vues minimales (MinUser, …) sont des sous-ensembles
// des données complètes renvoyées au runtime par loadUsers()/etc.
type EditActionOpts = Parameters<typeof window.showEditActionModal>[1];

// ════════════════════════════════════════════════════════════════════════════
// PAGE 1 — LISTE DES PLANS
// ════════════════════════════════════════════════════════════════════════════

type SortKey = 'code' | 'title' | 'date_start' | 'date_end' | 'avancement' | 'meteo';
let _plans: PlanRow[] = [];
let _sortKey: SortKey = 'code';
let _sortAsc = true;

pages.actionPlans = async function () {
  const canWrite = !!window.hasPermission?.('action_plans.write');
  setHeader(
    t('actionPlans.title'),
    canWrite
      ? `<button class="btn btn-primary ml-auto" id="btn-new-plan">${esc(t('actionPlans.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    _plans = (await api.getActionPlans()) as PlanRow[];
    renderPlansList();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function _sortPlans(rows: PlanRow[]): PlanRow[] {
  return domainSortPlans(rows, _sortKey, _sortAsc);
}

function _sortIndicator(key: SortKey): string {
  if (key !== _sortKey) return '';
  return _sortAsc ? ' ▲' : ' ▼';
}

function renderPlansList(): void {
  const canWrite = !!window.hasPermission?.('action_plans.write');
  if (!_plans.length) {
    setContent(
      `<div class="card"><div class="empty" style="padding:40px;text-align:center;color:var(--text3)">${esc(t('actionPlans.empty'))}</div></div>`,
    );
    if (canWrite)
      document
        .getElementById('btn-new-plan')
        ?.addEventListener('click', () => void showPlanModal());
    return;
  }
  const cols: Array<{ key: SortKey; label: string }> = [
    { key: 'code', label: t('actionPlans.col.code') },
    { key: 'title', label: t('actionPlans.col.title') },
    { key: 'date_start', label: t('actionPlans.col.dateStart') },
    { key: 'date_end', label: t('actionPlans.col.dateEnd') },
    { key: 'avancement', label: t('actionPlans.col.progress') },
    { key: 'meteo', label: t('actionPlans.col.weather') },
  ];
  const head = cols
    .map(
      (c) =>
        `<th data-sort="${c.key}" style="cursor:pointer;user-select:none">${esc(c.label)}${_sortIndicator(c.key)}</th>`,
    )
    .join('');
  const rows = _sortPlans(_plans)
    .map((p) => {
      const inactive = !p.is_active;
      const codeStyle = inactive ? 'color:var(--text3);font-style:italic' : '';
      const av = p.avancement == null ? '—' : `${p.avancement} %`;
      const meteo = p.meteo || '—';
      return `<tr data-plan-row="${p.id}">
        <td style="${codeStyle}"><a href="#action-plan/${p.id}" class="ap-link" style="font-family:var(--font-mono)">${esc(p.code)}</a>${inactive ? ` <span class="struct-badge">${esc(t('actionPlans.inactive'))}</span>` : ''}</td>
        <td><a href="#action-plan/${p.id}" class="ap-link">${esc(p.title)}</a></td>
        <td>${esc(fmtDate(p.date_start))}</td>
        <td>${esc(fmtDate(p.date_end))}</td>
        <td>${esc(av)}</td>
        <td>${esc(meteo)}</td>
        <td style="text-align:right;white-space:nowrap">
          ${canWrite ? `<button class="btn btn-secondary btn-icon" data-edit-plan="${p.id}" title="${esc(t('common.edit'))}" style="color:#147A57">✎</button>` : ''}
          ${canWrite ? `<button class="btn btn-danger btn-icon" data-del-plan="${p.id}" title="${esc(t('common.delete'))}">✕</button>` : ''}
        </td>
      </tr>`;
    })
    .join('');
  setContent(
    `<div class="card"><table class="data-table" id="plans-table">
       <thead><tr>${head}<th></th></tr></thead>
       <tbody>${rows}</tbody>
     </table></div>`,
  );
  if (canWrite)
    document.getElementById('btn-new-plan')?.addEventListener('click', () => void showPlanModal());

  const table = document.getElementById('plans-table');
  table?.querySelectorAll<HTMLElement>('th[data-sort]').forEach((th) => {
    th.addEventListener('click', () => {
      const key = th.dataset['sort'] as SortKey;
      if (key === _sortKey) _sortAsc = !_sortAsc;
      else {
        _sortKey = key;
        _sortAsc = true;
      }
      renderPlansList();
    });
  });
  table?.addEventListener('click', (e: Event) => {
    const target = (e.target as HTMLElement | null)?.closest('[data-edit-plan],[data-del-plan]');
    if (!(target instanceof HTMLElement)) return;
    const editId = target.dataset['editPlan'];
    if (editId !== undefined) {
      navigateTo('action-plan/:id', { id: parseInt(editId, 10) });
      return;
    }
    const delId = target.dataset['delPlan'];
    if (delId !== undefined) {
      const p = _plans.find((x) => x.id === parseInt(delId, 10));
      if (p) confirmDeletePlan(p);
    }
  });
}

function confirmDeletePlan(p: PlanRow): void {
  confirmDelete({
    titleKey: 'actionPlans.delete.title',
    bodyKey: 'actionPlans.delete.body',
    bodyParams: { label: `${p.code} — ${p.title}` },
    onDelete: () => api.deleteActionPlan(p.id),
    onDeleted: () => {
      toast(t('actionPlans.toast.deleted'));
      _plans = _plans.filter((x) => x.id !== p.id);
      renderPlansList();
    },
  });
}

/** Modale de création d'un plan (code/titre/dates/actif). Au succès, navigue
 *  vers la page de MAJ du plan (où l'on saisit les volets/phases). */
async function showPlanModal(): Promise<void> {
  const body = `
    <div class="form-row">
      <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.code'))}</label>
        <input type="text" id="ap-code" maxlength="5" placeholder="${esc(t('actionPlans.field.code.placeholder'))}" style="text-transform:uppercase;font-family:var(--font-mono);width:100%"></div>
      <div class="form-group" style="flex:3"><label>${esc(t('actionPlans.field.title'))}</label>
        <input type="text" id="ap-title" placeholder="${esc(t('actionPlans.field.title.placeholder'))}" style="width:100%"></div>
    </div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('actionPlans.field.description'))}</label>
      <textarea id="ap-desc" rows="2" style="width:100%"></textarea></div>
    <div class="form-row" style="margin-top:8px">
      <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.dateStart'))}</label>
        <input type="date" id="ap-start" style="width:100%"></div>
      <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.dateEnd'))}</label>
        <input type="date" id="ap-end" style="width:100%"></div>
    </div>
    <div class="form-group" style="margin-top:8px">
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;font-weight:400;margin:0">
        <input type="checkbox" id="ap-active" checked><span>${esc(t('actionPlans.field.active'))}</span>
      </label>
    </div>`;
  openModal(
    t('actionPlans.modal.create.title'),
    body,
    async () => {
      const code = (document.getElementById('ap-code') as HTMLInputElement | null)?.value
        .trim()
        .toUpperCase();
      const title = (document.getElementById('ap-title') as HTMLInputElement | null)?.value.trim();
      if (!code || !title) {
        toast(t('actionPlans.error.code_title_required'), 'error');
        return;
      }
      const payload = {
        code,
        title,
        description:
          (document.getElementById('ap-desc') as HTMLTextAreaElement | null)?.value.trim() || null,
        date_start: (document.getElementById('ap-start') as HTMLInputElement | null)?.value || null,
        date_end: (document.getElementById('ap-end') as HTMLInputElement | null)?.value || null,
        is_active: !!(document.getElementById('ap-active') as HTMLInputElement | null)?.checked,
      };
      try {
        const saved = (await api.createActionPlan(payload)) as PlanRow;
        closeModal();
        toast(t('actionPlans.toast.created'));
        navigateTo('action-plan/:id', { id: saved.id });
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    },
    t('common.add_short'),
  );
  setTimeout(() => {
    document.getElementById('ap-code')?.addEventListener('input', (e: Event) => {
      const tgt = e.target as HTMLInputElement | null;
      if (tgt) tgt.value = tgt.value.toUpperCase();
    });
  }, 0);
}

// ════════════════════════════════════════════════════════════════════════════
// PAGE 2 — MISE À JOUR D'UN PLAN (formulaire + arbre des phases)
// ════════════════════════════════════════════════════════════════════════════

let _plan: PlanDetail | null = null;
let _phases: PhaseT[] = [];
// `_expanded` : phases dont le sous-arbre des SUCCESSEURS est déplié.
let _expanded: Set<number> = new Set();
// `_actionsOpen` : phases dont le bloc ACTIONS est déplié (indépendant du
// sous-arbre, comme le repli par risque de #revision).
let _actionsOpen: Set<number> = new Set();
// Repli du volet d'informations du plan (haut de page), persisté localStorage.
const PLAN_PANEL_KEY = 'ap-plan-panel-collapsed';
// Données pour l'éditeur d'action (chargées paresseusement).
let _users: MinUser[] = [];
let _statuses: MinStatus[] = [];
let _projects: MinProject[] = [];

// Tri des colonnes d'actions (commun à toutes les phases) + affichage des
// actions terminées (défaut off) + phase à faire clignoter après matérialisation.
let _actSortKey = 'code';
let _actSortDir: 'asc' | 'desc' = 'asc';
let _showDoneActions = false;
let _blinkPhaseId: number | null = null;

/** Ids des statuts finaux (actions terminées) — pour le filtre « afficher terminées ». */
function _finalStatusIds(): Set<number> {
  return new Set(_statuses.filter((s) => s.is_final).map((s) => s.id));
}

/** Actions d'une phase à afficher : filtre « terminées » (si off) puis tri courant. */
function _visibleActions(p: PhaseT): RowAction[] {
  const acts = (p.actions || []) as RowAction[];
  const filtered = filterCompletedActions(
    acts as Array<RowAction & { status_id?: number | null }>,
    _finalStatusIds(),
    _showDoneActions,
  );
  const userMap = Object.fromEntries(_users.map((u) => [u.id, u]));
  const statusMap = Object.fromEntries(_statuses.map((s) => [s.id, s]));
  const projectMap = Object.fromEntries(_projects.map((pr) => [pr.id, pr]));
  const todayStr = new Date().toISOString().slice(0, 10);
  const nameList = (ids: unknown): string =>
    Array.isArray(ids)
      ? ids
          .map((i) => {
            const uid = typeof i === 'object' && i ? (i as { id?: number }).id : (i as number);
            return uid != null ? userMap[uid]?.trigram || userMap[uid]?.name || '' : '';
          })
          .join(', ')
      : '';
  return domainSortActions(filtered as never[], _actSortKey, _actSortDir, {
    nameOf: nameList,
    statusName: (id) => (id != null ? statusMap[id]?.name : '') || '',
    projectCode: (id) => projectMap[id]?.code || '',
    today: todayStr,
  }) as RowAction[];
}

// Référentiels pour les critères d'une phase Intelligente (chargés à l'ouverture
// de la modale). Projets : on réutilise `_projects` déjà chargé.
interface MinRef {
  id: number;
  code?: string | null;
  name?: string | null;
  label?: string | null;
}
let _programs: MinRef[] = [];
let _structures: MinRef[] = [];
let _committees: MinRef[] = [];
let _smartOptionsLoaded = false;

async function _loadSmartOptions(): Promise<void> {
  if (_smartOptionsLoaded) return;
  // Structures via l'endpoint /options OUVERT (structures.read est fermé).
  const [pg, st, cm] = await Promise.all([
    (api.getPrograms() as Promise<MinRef[]>).catch(() => [] as MinRef[]),
    (api.getStructuresForFilter() as Promise<MinRef[]>).catch(() => [] as MinRef[]),
    (api.getCommittees() as Promise<MinRef[]>).catch(() => [] as MinRef[]),
  ]);
  _programs = pg;
  _structures = st;
  _committees = cm;
  _smartOptionsLoaded = true;
}

// Persistance de l'état plié/déplié des phases (sous-arbre `_expanded` + panneau
// d'actions `_actionsOpen`), par plan, en localStorage — restauré au retour.
function _treeStateKey(planId: number): string {
  return `ap-tree-state:${planId}`;
}

function _saveTreeState(): void {
  if (!_plan) return;
  try {
    tlsSet(_treeStateKey(_plan.id), JSON.stringify({ e: [..._expanded], a: [..._actionsOpen] }));
  } catch {
    /* localStorage indispo : état non persisté, gracieux */
  }
}

/** Restaure l'état plié/déplié depuis localStorage ; par défaut tout déplié. */
function _restoreTreeState(planId: number, phases: PhaseT[]): void {
  const ids = new Set(phases.map((p) => p.id));
  let saved: { e?: number[]; a?: number[] } | null = null;
  try {
    const raw = tlsGet(_treeStateKey(planId));
    if (raw) saved = JSON.parse(raw) as { e?: number[]; a?: number[] };
  } catch {
    saved = null;
  }
  if (saved) {
    // Intersecte avec les phases existantes (une phase supprimée disparaît).
    _expanded = new Set((saved.e || []).filter((x) => ids.has(x)));
    _actionsOpen = new Set((saved.a || []).filter((x) => ids.has(x)));
  } else {
    _expanded = new Set(ids);
    _actionsOpen = new Set(ids);
  }
}

function _planPanelCollapsed(): boolean {
  try {
    return tlsGet(PLAN_PANEL_KEY) === '1';
  } catch {
    return false;
  }
}

pages.actionPlan = async function (params: Record<string, string>) {
  const id = parseInt(params['id'] || '0', 10);
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    _plan = (await api.getActionPlan(id)) as PlanDetail;
    _phases = _plan.phases || [];
    // Restaure l'état plié/déplié mémorisé pour ce plan (sinon tout déplié).
    _restoreTreeState(id, _phases);
    // Référentiels nécessaires au rendu du tableau d'actions (maps user/projet/
    // statut consommées par componentActionRow) ET à l'éditeur d'action.
    const [u, s, p] = await Promise.all([
      (loadUsers() as Promise<MinUser[]>).catch(() => [] as MinUser[]),
      (loadActionStatuses() as Promise<MinStatus[]>).catch(() => [] as MinStatus[]),
      (api.getProjects() as Promise<MinProject[]>).catch(() => [] as MinProject[]),
    ]);
    _users = u;
    _statuses = s;
    _projects = p;
    renderPlanPage();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function _canWrite(): boolean {
  return !!window.hasPermission?.('action_plans.write');
}

function renderPlanPage(): void {
  if (!_plan) return;
  const p = _plan;
  const ro = !_canWrite();
  const dlIcon = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 4v9"/><path d="M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>`;
  setHeader(
    `<a href="#action-plans" style="text-decoration:none">${esc(t('actionPlans.title'))}</a>
     <span style="color:var(--text3)"> / </span>${esc(p.code)} — ${esc(p.title)}`,
    `<button class="btn btn-secondary ml-auto" id="btn-export-plan" aria-haspopup="menu" style="display:inline-flex;align-items:center">${dlIcon}${esc(t('pageActions.btn.export'))} ▾</button>`,
  );
  // Volet « informations du plan » repliable (chevron dans l'en-tête). Le repli
  // ne re-render pas la page (on masque juste le corps) pour ne pas perdre une
  // saisie en cours ; l'état est persisté en localStorage.
  const collapsed = _planPanelCollapsed();
  const planForm = `
    <div class="card" style="margin-bottom:16px">
      <div id="pf-head" style="display:flex;align-items:center;gap:8px;cursor:pointer;user-select:none">
        <button class="struct-caret" id="pf-toggle" aria-expanded="${collapsed ? 'false' : 'true'}" title="${esc(t('actionPlans.panel.toggle'))}">${collapsed ? '▶' : '▼'}</button>
        <strong style="flex:1">${esc(t('actionPlans.panel.title'))}</strong>
        ${collapsed ? `<span style="color:var(--text3);font-size:12px">${esc(p.code)} — ${esc(p.title)}</span>` : ''}
      </div>
      <div id="pf-body" style="${collapsed ? 'display:none' : ''};margin-top:10px">
        <div class="form-row">
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.code'))}</label>
            <input type="text" id="pf-code" maxlength="5" value="${esc(p.code)}" ${ro ? 'disabled' : ''} style="text-transform:uppercase;font-family:var(--font-mono);width:100%"></div>
          <div class="form-group" style="flex:3"><label>${esc(t('actionPlans.field.title'))}</label>
            <input type="text" id="pf-title" value="${esc(p.title)}" ${ro ? 'disabled' : ''} style="width:100%"></div>
        </div>
        <div class="form-group" style="margin-top:8px"><label>${esc(t('actionPlans.field.description'))}</label>
          <textarea id="pf-desc" rows="2" ${ro ? 'disabled' : ''} style="width:100%">${esc(p.description || '')}</textarea></div>
        <div class="form-row" style="margin-top:8px">
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.dateStart'))}</label>
            <input type="date" id="pf-start" value="${esc(p.date_start || '')}" ${ro ? 'disabled' : ''} style="width:100%"></div>
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.dateEnd'))}</label>
            <input type="date" id="pf-end" value="${esc(p.date_end || '')}" ${ro ? 'disabled' : ''} style="width:100%"></div>
          <div class="form-group" style="flex:1;display:flex;align-items:flex-end">
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;font-weight:400;margin:0">
              <input type="checkbox" id="pf-active" ${p.is_active ? 'checked' : ''} ${ro ? 'disabled' : ''}><span>${esc(t('actionPlans.field.active'))}</span>
            </label>
          </div>
        </div>
        ${ro ? '' : `<div style="margin-top:12px;text-align:right"><button class="btn btn-primary" id="pf-save">${esc(t('common.save'))}</button></div>`}
      </div>
    </div>`;

  const phasesHeader = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
      <h3 style="margin:0;flex:1">${esc(t('actionPlans.phases.title'))}</h3>
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:12px;font-weight:400;margin:0;white-space:nowrap">
        <input type="checkbox" id="ap-show-done" ${_showDoneActions ? 'checked' : ''}><span>${esc(t('actionPlans.action.showDone'))}</span>
      </label>
      <button class="btn btn-secondary btn-sm" id="btn-collapse-all">${esc(t('actionPlans.phases.collapseAll'))}</button>
      <button class="btn btn-secondary btn-sm" id="btn-expand-all">${esc(t('actionPlans.phases.expandAll'))}</button>
      ${ro ? '' : `<button class="btn btn-primary" id="btn-new-phase">${esc(t('actionPlans.phases.add'))}</button>`}
    </div>`;

  setContent(`${planForm}${phasesHeader}<div id="phases-host"></div>`);

  // Repli du volet plan (toggle DOM direct, sans re-render).
  document.getElementById('pf-head')?.addEventListener('click', (e: Event) => {
    // Ignore les clics dans le corps (champs) — seul l'en-tête plie.
    if ((e.target as HTMLElement | null)?.closest('#pf-body')) return;
    const body = document.getElementById('pf-body');
    const caret = document.getElementById('pf-toggle');
    if (!body || !caret) return;
    const nowCollapsed = body.style.display !== 'none';
    body.style.display = nowCollapsed ? 'none' : '';
    caret.textContent = nowCollapsed ? '▶' : '▼';
    caret.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
    try {
      tlsSet(PLAN_PANEL_KEY, nowCollapsed ? '1' : '0');
    } catch {
      /* localStorage indispo : repli non persisté, gracieux */
    }
  });

  document.getElementById('btn-collapse-all')?.addEventListener('click', () => {
    _expanded.clear();
    _actionsOpen.clear();
    renderPhasesTree();
  });
  document.getElementById('btn-expand-all')?.addEventListener('click', () => {
    _expanded = new Set(_phases.map((x) => x.id));
    _actionsOpen = new Set(_phases.map((x) => x.id));
    renderPhasesTree();
  });
  document.getElementById('btn-export-plan')?.addEventListener('click', (e: Event) => {
    openExportMenu(e.currentTarget as HTMLElement, (fmt) =>
      _downloadPlanExport(fmt === 'json' ? 'json' : 'xlsx'),
    );
  });
  document.getElementById('ap-show-done')?.addEventListener('change', (e: Event) => {
    _showDoneActions = (e.target as HTMLInputElement).checked;
    renderPhasesTree();
  });

  if (!ro) {
    document.getElementById('pf-code')?.addEventListener('input', (e: Event) => {
      const tgt = e.target as HTMLInputElement | null;
      if (tgt) tgt.value = tgt.value.toUpperCase();
    });
    document.getElementById('pf-save')?.addEventListener('click', () => void savePlanForm());
    document
      .getElementById('btn-new-phase')
      ?.addEventListener('click', () => void showPhaseModal(null));
  }
  renderPhasesTree();
}

async function savePlanForm(): Promise<void> {
  if (!_plan) return;
  const code = (document.getElementById('pf-code') as HTMLInputElement | null)?.value
    .trim()
    .toUpperCase();
  const title = (document.getElementById('pf-title') as HTMLInputElement | null)?.value.trim();
  if (!code || !title) {
    toast(t('actionPlans.error.code_title_required'), 'error');
    return;
  }
  const payload = {
    code,
    title,
    description:
      (document.getElementById('pf-desc') as HTMLTextAreaElement | null)?.value.trim() || null,
    date_start: (document.getElementById('pf-start') as HTMLInputElement | null)?.value || null,
    date_end: (document.getElementById('pf-end') as HTMLInputElement | null)?.value || null,
    is_active: !!(document.getElementById('pf-active') as HTMLInputElement | null)?.checked,
  };
  try {
    const saved = (await api.updateActionPlan(_plan.id, payload)) as PlanDetail;
    _plan = saved;
    _phases = saved.phases || _phases;
    toast(t('actionPlans.toast.updated'));
    renderPlanPage();
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
  }
}

// ── Export du plan (menu XLS/JSON, UX reprise de #actions) ────────────────────
// Le menu de formats est factorisé dans utils/export-menu (juin 2026).

async function _downloadPlanExport(fmt: 'xlsx' | 'json'): Promise<void> {
  if (!_plan) return;
  try {
    const blob = (await api.exportActionPlan(_plan.id, {
      fmt,
      export_dt: new Date().toISOString(),
    })) as Blob;
    const ext = fmt === 'xlsx' ? 'xlsx' : 'json';
    api.triggerBlobDownload(
      blob,
      `plan_${_plan.code}_${new Date().toISOString().slice(0, 10)}.${ext}`,
    );
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
  }
}

// ── Arbre des phases (UX reprise de #admin/structures) ────────────────────────

/** Map les phases vers la forme {id, parent_ids} attendue par structure-graph,
 *  où « parent » = prédécesseur. */
function _asNodes(): Array<{ id: number; parent_ids: number[] }> {
  return _phases.map((p) => ({ id: p.id, parent_ids: p.predecessor_ids || [] }));
}

function _byId(): Map<number, PhaseT> {
  return new Map(_phases.map((p) => [p.id, p]));
}

function _childrenMap(): Map<number, number[]> {
  const m = graphChildrenMap(_asNodes());
  const byId = _byId();
  for (const [, ids] of m) {
    ids.sort((a, b) => (byId.get(a)?.code || '').localeCompare(byId.get(b)?.code || ''));
  }
  return m;
}

function _descendantIds(rootId: number): Set<number> {
  return graphDescendantIds(_asNodes(), rootId);
}

function renderPhasesTree(): void {
  // Persiste l'état plié/déplié courant à chaque rendu (point unique de mutation).
  _saveTreeState();
  const host = document.getElementById('phases-host');
  if (!host) return;
  const ro = !_canWrite();
  if (!_phases.length) {
    host.innerHTML = `<div class="card"><div class="empty" style="padding:32px;text-align:center;color:var(--text3)">${esc(t('actionPlans.phases.empty'))}</div></div>`;
    return;
  }
  const byId = _byId();
  const children = _childrenMap();
  const roots = _phases
    .filter((p) => !(p.predecessor_ids || []).length)
    .sort((a, b) => a.code.localeCompare(b.code));

  const rootzone = ro
    ? ''
    : `<div class="struct-rootzone" data-rootzone>${esc(t('actionPlans.phases.rootzone'))}</div>`;
  const nodes = roots.map((r) => buildPhaseNode(r.id, null, 0, new Set(), byId, children)).join('');
  // `.join('')` (CallExpression) plutôt qu'une variable : conforme à la règle
  // eslint anti-XSS sur innerHTML. Le contenu est intégralement esc()'d.
  host.innerHTML = `<div class="card">${rootzone}<div id="phase-tree" class="struct-tree">${nodes}</div>${rootzone}</div>`;

  const tree = document.getElementById('phase-tree');
  if (tree) {
    tree.addEventListener('click', onTreeClick);
    if (!ro) {
      tree.addEventListener('dragstart', onTreeDragStart);
      tree.addEventListener('dragover', onTreeDragOver);
      tree.addEventListener('dragleave', onTreeDragLeave);
      tree.addEventListener('drop', onTreeDrop);
      tree.addEventListener('dragend', onTreeDragEnd);
    }
  }
  if (!ro) {
    document.querySelectorAll<HTMLElement>('[data-rootzone]').forEach((rz) => {
      rz.addEventListener('dragover', onRootZoneOver);
      rz.addEventListener('dragleave', onRootZoneLeave);
      rz.addEventListener('drop', onRootZoneDrop);
    });
  }
}

/** Alerte d'échéance d'une phase (cf. domain/action-plan.phaseDeadlineAlert).
 *  Le cast restreint PhaseT à la forme DeadlinePhase attendue. */
function _phaseDeadlineAlert(p: PhaseT): boolean {
  return domainPhaseDeadlineAlert(p);
}

function buildPhaseNode(
  id: number,
  predId: number | null,
  depth: number,
  path: Set<number>,
  byId: Map<number, PhaseT>,
  children: Map<number, number[]>,
): string {
  const p = byId.get(id);
  if (!p) return '';
  if (path.has(id)) {
    return `<div class="struct-row" style="padding-left:${12 + depth * 22}px;color:var(--warn)">↻ ${esc(p.code)} — ${esc(t('actionPlans.phases.cycle'))}</div>`;
  }
  const ro = !_canWrite();
  const kids = children.get(id) || [];
  const hasKids = kids.length > 0;
  const isOpen = _expanded.has(id);
  const actionsOpen = _actionsOpen.has(id);
  const isSmart = !!p.is_smart;
  const caret = hasKids
    ? `<button class="struct-caret" data-toggle="${id}">${isOpen ? '▼' : '▶'}</button>`
    : `<span class="struct-caret struct-caret-empty"></span>`;
  // Compteur = actions effectivement affichées (filtre « terminées » appliqué).
  const actionCount = _visibleActions(p).length;
  // Alerte : l'action la plus tardive (date révisée si elle existe, sinon
  // butoir) dépasse la date de fin cible de la phase. Comparaison de dates ISO
  // (YYYY-MM-DD) → l'ordre lexical suffit.
  const alert = _phaseDeadlineAlert(p);
  const alertIcon = alert
    ? ` <span title="${esc(t('actionPlans.phases.dateAlert'))}" style="color:var(--danger);cursor:help;font-size:12px">⚠</span>`
    : '';
  const dates =
    p.date_start || p.date_end_target
      ? `<span style="color:var(--text3);font-size:11px;margin-left:8px">${esc(fmtDate(p.date_start))} → ${esc(fmtDate(p.date_end_target))}${alertIcon}</span>`
      : '';

  let row = `
    <div class="struct-row${predId === null ? ' struct-root' : ''}" ${ro ? '' : 'draggable="true"'} data-node="${id}" data-parent="${predId ?? ''}" style="padding-left:${12 + depth * 22}px">
      ${ro ? '' : `<span class="struct-grip" title="${esc(t('actionPlans.phases.dnd.hint'))}">⠿</span>`}
      ${caret}
      <span class="struct-code">${esc(p.code)}</span>
      <span class="struct-label">${esc(p.title)}</span>
      ${phaseKpiPastilles(p.health)}
      ${dates}
      <span class="struct-badge" title="${esc(t('actionPlans.phases.actionCount'))}">${actionCount} ${esc(t('actionPlans.phases.actionsShort'))}</span>
      <span class="struct-actions">
        <button class="btn btn-secondary btn-icon" data-toggle-actions="${id}" title="${esc(t('actionPlans.action.toggle'))}">${actionsOpen ? '▼' : '▶'}</button>
        <button class="btn btn-secondary btn-sm" data-kpi-phase="${id}" title="${esc(t('actionPlans.kpi.cta.title'))}">${esc(t('actionPlans.kpi.cta'))}</button>
        ${isSmart && !ro ? `<button class="btn btn-secondary btn-sm" data-materialize="${id}" title="${esc(t('actionPlans.phases.materialize.title'))}">${esc(t('actionPlans.phases.materialize.cta'))}</button>` : ''}
        ${ro ? '' : `<button class="btn btn-secondary btn-icon" data-edit-phase="${id}" title="${esc(t('common.edit'))}" style="color:#147A57">✎</button>`}
        ${ro ? '' : `<button class="btn btn-danger btn-icon" data-del-phase="${id}" title="${esc(t('common.delete'))}">✕</button>`}
      </span>
    </div>`;

  // Panneau d'actions de la phase (UX/UI reprise de #revision : carte bordée
  // verte `.risk-actions-panel`, titre à gauche, CTA Lier/Copier/Nouvelle à
  // droite). Affiché quand le toggle ▼ de la ligne est ouvert ; indépendant du
  // sous-arbre des successeurs.
  if (actionsOpen) row += buildPhaseActions(p, depth);

  // Sous-arbre des successeurs (déplié via le caret de gauche).
  if (hasKids && isOpen) {
    const childPath = new Set(path);
    childPath.add(id);
    for (const cid of kids) row += buildPhaseNode(cid, id, depth + 1, childPath, byId, children);
  }
  return row;
}

function buildPhaseActions(p: PhaseT, depth: number): string {
  const isSmart = !!p.is_smart;
  // Phase Intelligente : actions CALCULÉES → pas de liaison/édition manuelle ici
  // (lecture seule), CTA masqués, ✕ « délier » désactivé (backend renvoie 409).
  const ro = !_canWrite() || isSmart;
  const pad = 12 + (depth + 1) * 22;
  const acts = _visibleActions(p);
  // Titre du panneau, repris de #revision : « Plan d'actions · N action(s) ».
  const title = t(
    acts.length === 1 ? 'pageProject.actions.titleOne' : 'pageProject.actions.titleMany',
    {
      n: acts.length,
      singular: t('vocabulary.action'),
      plural: t('vocabulary.action_plural'),
    },
  );
  const ctas = isSmart
    ? `<span style="font-size:11px;font-style:italic;color:var(--text3)">${esc(t('actionPlans.action.computed'))}</span>`
    : ro
      ? ''
      : `<div style="display:flex;gap:6px;align-items:center">
         <button class="btn btn-secondary btn-sm" data-link-action="${p.id}">${esc(t('pageProject.actions.btnLink'))}</button>
         <button class="btn btn-secondary btn-sm" data-copy-action="${p.id}">${esc(t('pageProject.actions.btnCopy'))}</button>
         <button class="btn btn-secondary btn-sm" data-add-action="${p.id}">${esc(t('pageProject.actions.btnNew'))}</button>
       </div>`;
  // Tableau d'actions au format EXACT de #revision (composant partagé) :
  // colonnes #, Titre, État/Avanc., Priorité, Fin, RACI, Projets, Risques,
  // Modifié le. `riskId`/`idx` activent les boutons inline ✎ (édition) et ✕
  // (ici : délier l'action de la phase) — câblés dans onTreeClick.
  const cr = window.componentActionRow;
  const userMap = Object.fromEntries(_users.map((u) => [u.id, u]));
  const projectMap = Object.fromEntries(_projects.map((pr) => [pr.id, pr]));
  const rowOpts = {
    statuses: _statuses,
    userMap,
    projectMap,
    readOnly: ro,
    riskId: p.id,
    idx: 0,
    // Le bouton inline délie l'action de la phase (≠ suppression) → picto « délier ».
    unlink: true,
  } as unknown as RowOpts;
  const rowsHtml = acts.map((a, idx) => cr.buildActionRowHtml(a, { ...rowOpts, idx })).join('');
  // En-têtes triables (clic → onTreeClick gère `th[data-sort]`).
  const sortTh = (key: string, labelHtml: string): string =>
    cr.buildSortTh(key, labelHtml, _actSortKey, _actSortDir);
  const body = acts.length
    ? `<table class="action-table"><thead><tr>${cr.buildActionHeadersHtml({ sortFn: sortTh })}</tr></thead><tbody>${rowsHtml}</tbody></table>`
    : `<div style="font-size:12px;color:var(--text3);font-style:italic">${esc(t('actionPlans.action.none'))}</div>`;
  // Carte bordée verte identique à #revision (.risk-actions-panel) : titre à
  // gauche, CTA verts (btn-secondary) à droite, lignes d'actions en dessous.
  // `phase-actions-blink` : clignotement après matérialisation (→ statique).
  const blink = _blinkPhaseId === p.id ? ' phase-actions-blink' : '';
  return `<div class="risk-actions-panel${blink}" style="margin:4px 0 10px ${pad}px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
        <span style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--text3)">${esc(title)}</span>
        ${ctas}
      </div>
      ${body}
    </div>`;
}

function onTreeClick(e: Event): void {
  const el = e.target as HTMLElement | null;
  // 1. Tri des colonnes d'actions (commun à toutes les phases).
  const sortTh = el?.closest<HTMLElement>('th[data-sort]');
  if (sortTh) {
    const key = sortTh.dataset['sort'] || 'code';
    if (_actSortKey === key) _actSortDir = _actSortDir === 'asc' ? 'desc' : 'asc';
    else {
      _actSortKey = key;
      _actSortDir = 'asc';
    }
    renderPhasesTree();
    return;
  }
  const target = el?.closest(
    '[data-toggle],[data-toggle-actions],[data-kpi-phase],[data-materialize],[data-edit-phase],[data-del-phase],[data-link-action],[data-copy-action],[data-add-action],[data-edit-action-risk],[data-del-action-risk]',
  );
  if (!(target instanceof HTMLElement)) {
    // 2. Clic sur la barre d'une phase (hors contrôle) → plie/déplie le panneau
    //    d'actions (même comportement que le bouton ▶/▼).
    const row = el?.closest<HTMLElement>('.struct-row');
    const nodeId = row?.dataset['node'];
    if (nodeId) {
      const id = parseInt(nodeId, 10);
      if (_actionsOpen.has(id)) _actionsOpen.delete(id);
      else _actionsOpen.add(id);
      renderPhasesTree();
    }
    return;
  }
  const kpiPhase = target.dataset['kpiPhase'];
  if (kpiPhase !== undefined && _plan) {
    navigateTo('action-plan/:id/phase/:phaseId/kpi', {
      id: _plan.id,
      phaseId: parseInt(kpiPhase, 10),
    });
    return;
  }
  const materializeId = target.dataset['materialize'];
  if (materializeId !== undefined) {
    const p = _phases.find((x) => x.id === parseInt(materializeId, 10));
    if (p) confirmMaterializePhase(p);
    return;
  }
  const toggle = target.dataset['toggle'];
  if (toggle !== undefined) {
    const id = parseInt(toggle, 10);
    if (_expanded.has(id)) _expanded.delete(id);
    else _expanded.add(id);
    renderPhasesTree();
    return;
  }
  const toggleActions = target.dataset['toggleActions'];
  if (toggleActions !== undefined) {
    const id = parseInt(toggleActions, 10);
    if (_actionsOpen.has(id)) _actionsOpen.delete(id);
    else _actionsOpen.add(id);
    renderPhasesTree();
    return;
  }
  const editId = target.dataset['editPhase'];
  if (editId !== undefined) {
    const p = _phases.find((x) => x.id === parseInt(editId, 10));
    if (p) void showPhaseModal(p);
    return;
  }
  const delId = target.dataset['delPhase'];
  if (delId !== undefined) {
    const p = _phases.find((x) => x.id === parseInt(delId, 10));
    if (p) confirmDeletePhase(p);
    return;
  }
  const linkId = target.dataset['linkAction'];
  if (linkId !== undefined) {
    void showLinkActionModal(parseInt(linkId, 10));
    return;
  }
  const copyId = target.dataset['copyAction'];
  if (copyId !== undefined) {
    void showCopyActionModal(parseInt(copyId, 10));
    return;
  }
  const addId = target.dataset['addAction'];
  if (addId !== undefined) {
    openCreateActionForPhase(parseInt(addId, 10));
    return;
  }
  // Édition inline (✎ / clic sur #id ou titre) : data-edit-action-risk = phase,
  // data-edit-action-inline = index de l'action dans la phase → page #action/:id.
  const editRisk = target.dataset['editActionRisk'];
  if (editRisk !== undefined) {
    const act = _actionAt(
      parseInt(editRisk, 10),
      parseInt(target.dataset['editActionInline'] || '', 10),
    );
    // Action confidentielle masquée : pas d'édition (l'éditeur renverrait 404).
    if ((act as { masked?: boolean } | undefined)?.masked) return;
    if (act?.id) {
      // #13 : mémoriser la page d'origine (MAJ du plan) pour que l'éditeur
      // d'action y revienne après enregistrement, au lieu de #actions.
      try {
        sessionStorage.setItem('actionEditor:backHash', window.location.hash);
      } catch {
        /* sessionStorage indispo : retour par défaut #actions */
      }
      navigateTo('action/:id', { id: act.id });
    }
    return;
  }
  // Bouton « délier » : détache l'action de la phase (≠ suppression) — confirmé.
  const delRisk = target.dataset['delActionRisk'];
  if (delRisk !== undefined) {
    const phaseId = parseInt(delRisk, 10);
    const act = _actionAt(phaseId, parseInt(target.dataset['delActionIdx'] || '', 10));
    if (act?.id) confirmUnlinkAction(phaseId, act.id);
  }
}

/** Confirme puis délie l'action de la phase. */
function confirmUnlinkAction(phaseId: number, actionId: number): void {
  openModal(
    t('actionPlans.action.unlink.title'),
    `<p>${esc(t('actionPlans.action.unlink.body'))}</p>`,
    async () => {
      closeModal();
      await unlinkAction(phaseId, actionId);
    },
    t('actionPlans.action.unlink.confirm'),
  );
}

/** Action à l'index `idx` de la phase `phaseId` — indexée sur les actions AFFICHÉES
 *  (filtre « terminées » + tri courant), pour rester aligné avec le rendu. */
function _actionAt(phaseId: number, idx: number): RowAction | undefined {
  if (Number.isNaN(phaseId) || Number.isNaN(idx)) return undefined;
  const p = _phases.find((x) => x.id === phaseId);
  return p ? _visibleActions(p)[idx] : undefined;
}

/** « → statique » : confirme puis matérialise la phase intelligente (fige ses
 *  actions en liens manuels, stoppe le recalcul auto) + clignotement. */
function confirmMaterializePhase(p: PhaseT): void {
  openModal(
    t('actionPlans.phases.materialize.title'),
    `<p>${esc(t('actionPlans.phases.materialize.body'))}</p>`,
    async () => {
      try {
        await api.materializePhase(p.id);
        closeModal();
        toast(t('actionPlans.phases.materialize.done'));
        _actionsOpen.add(p.id);
        _blinkPhaseId = p.id;
        await _refreshPlan();
        // Retire le marqueur de clignotement après l'animation.
        setTimeout(() => {
          if (_blinkPhaseId === p.id) {
            _blinkPhaseId = null;
            renderPhasesTree();
          }
        }, 1400);
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    },
    t('actionPlans.phases.materialize.confirm'),
  );
}

// ── Drag & drop des prédécesseurs (identique à structures) ────────────────────

let _drag: { child: number; fromParent: number | null } | null = null;

function _targetStatus(targetId: number): DropStatus {
  return domainDropTargetStatus(
    _drag,
    targetId,
    _drag ? _descendantIds(_drag.child) : new Set<number>(),
  );
}

// Les trois gestionnaires viennent de `utils/tree-dnd.ts` : ils étaient
// dupliqués avec `page-admin-structures.ts`, dont `onTreeDragStart` byte pour
// byte (audit front du 2026-09-10).
const {
  onDragStart: onTreeDragStart,
  onDragOver: onTreeDragOver,
  onDrop: onTreeDrop,
} = createTreeDndHandlers({
  getDrag: () => _drag,
  setDrag: (d) => {
    _drag = d;
  },
  descendantIds: _descendantIds,
  targetStatus: _targetStatus,
  applyRelink: (child, fromParent, target, add) => {
    void applyRelink(child, fromParent, target, add);
  },
});

function onTreeDragLeave(e: DragEvent): void {
  rowFromEvent(e)?.classList.remove('struct-drop-target', 'struct-drop-invalid');
}

function onTreeDragEnd(): void {
  _drag = null;
  clearDropHints();
  document
    .querySelectorAll('.struct-dragging')
    .forEach((el) => el.classList.remove('struct-dragging'));
}

function onRootZoneOver(e: DragEvent): void {
  if (!_drag || _drag.fromParent === null) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  (e.currentTarget as HTMLElement).classList.add('dnd-over');
}

function onRootZoneLeave(e: DragEvent): void {
  (e.currentTarget as HTMLElement).classList.remove('dnd-over');
}

function onRootZoneDrop(e: DragEvent): void {
  if (!_drag || _drag.fromParent === null) return;
  e.preventDefault();
  const drag = _drag;
  void applyRelink(drag.child, drag.fromParent, null, false);
}

async function applyRelink(
  child: number,
  fromParent: number | null,
  targetParent: number | null,
  add: boolean,
): Promise<void> {
  const phase = _phases.find((x) => x.id === child);
  if (!phase) return;
  const predecessor_ids = computeRelinkParents(
    phase.predecessor_ids || [],
    fromParent,
    targetParent,
    add,
  );
  const before = phase.predecessor_ids || [];
  if (relinkChangesNothing(before, predecessor_ids)) return;
  await persistPhase(phase, { predecessor_ids });
}

/** Persiste une phase (PUT) avec ses champs courants + overrides éventuels. */
async function persistPhase(phase: PhaseT, override: Partial<PhaseT>): Promise<void> {
  const merged = { ...phase, ...override };
  try {
    const saved = (await api.updatePhase(phase.id, {
      code: merged.code,
      title: merged.title,
      description: merged.description || null,
      date_start: merged.date_start || null,
      date_end_target: merged.date_end_target || null,
      predecessor_ids: merged.predecessor_ids || [],
      // Conserver le caractère Intelligent + ses critères : un PUT « partiel »
      // (drag&drop de prédécesseur) ne doit pas réinitialiser la phase en manuelle.
      is_smart: !!merged.is_smart,
      smart_program_id: merged.smart_program_id ?? null,
      smart_project_id: merged.smart_project_id ?? null,
      smart_structure_id: merged.smart_structure_id ?? null,
      smart_committee_id: merged.smart_committee_id ?? null,
    })) as PhaseT;
    _replacePhase(saved);
    renderPhasesTree();
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
  }
}

function _replacePhase(saved: PhaseT): void {
  const idx = _phases.findIndex((x) => x.id === saved.id);
  // Conserve la liste d'actions déjà chargée si le PUT ne la renvoie pas.
  if (idx !== -1) {
    const prev = _phases[idx];
    const merged: PhaseT = { ...saved };
    if (saved.actions === undefined && prev?.actions !== undefined) merged.actions = prev.actions;
    _phases[idx] = merged;
  } else {
    _phases.push(saved);
    _expanded.add(saved.id);
  }
}

// ── Modale phase (création / édition + prédécesseurs en cases à cocher) ───────

async function showPhaseModal(phase: PhaseT | null): Promise<void> {
  if (!_plan) return;
  const planId = _plan.id;
  const isNew = !phase;
  await _loadSmartOptions();
  const ph: PhaseT = phase || {
    id: 0,
    plan_id: planId,
    code: '',
    title: '',
    description: null,
    date_start: null,
    date_end_target: null,
    predecessor_ids: [],
    avancement: null,
  };
  // Prédécesseurs sélectionnables : toutes les phases sauf elle-même et ses
  // successeurs (anti-cycle ; le backend revérifie).
  const excluded = isNew ? new Set<number>() : _descendantIds(ph.id);
  if (!isNew) excluded.add(ph.id);
  const selectable = _phases
    .filter((x) => !excluded.has(x.id))
    .sort((a, b) => a.code.localeCompare(b.code));
  const current = new Set(ph.predecessor_ids || []);
  const predHtml = selectable.length
    ? selectable
        .map(
          (x) => `<label class="struct-parent-opt">
            <input type="checkbox" class="ph-pred-chk" value="${x.id}" ${current.has(x.id) ? 'checked' : ''}>
            <span><strong>${esc(x.code)}</strong> — ${esc(x.title)}</span>
          </label>`,
        )
        .join('')
    : `<p style="font-size:12px;color:var(--text3);margin:0">${esc(t('actionPlans.phases.noPred'))}</p>`;

  const body = `
    <div class="form-row">
      <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.field.code'))}</label>
        <input type="text" id="ph-code" maxlength="5" value="${esc(ph.code)}" style="text-transform:uppercase;font-family:var(--font-mono);width:100%"></div>
      <div class="form-group" style="flex:3"><label>${esc(t('actionPlans.phases.field.title'))}</label>
        <input type="text" id="ph-title" value="${esc(ph.title)}" style="width:100%"></div>
    </div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('actionPlans.phases.field.description'))}</label>
      <textarea id="ph-desc" rows="2" style="width:100%">${esc(ph.description || '')}</textarea></div>
    <div class="form-row" style="margin-top:8px">
      <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.phases.field.dateStart'))}</label>
        <input type="date" id="ph-start" value="${esc(ph.date_start || '')}" style="width:100%"></div>
      <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.phases.field.dateEnd'))}</label>
        <input type="date" id="ph-end" value="${esc(ph.date_end_target || '')}" style="width:100%"></div>
    </div>
    <div class="form-group" style="margin-top:10px">
      <label>${esc(t('actionPlans.phases.field.predecessors'))}</label>
      <p style="font-size:11px;color:var(--text3);margin:0 0 6px">${esc(t('actionPlans.phases.field.predecessors.help'))}</p>
      <div class="struct-parents-box">${predHtml}</div>
    </div>
    ${_smartFieldsHtml(ph)}`;

  openModal(
    isNew ? t('actionPlans.phases.modal.create') : t('actionPlans.phases.modal.update'),
    body,
    async () => {
      const code = (document.getElementById('ph-code') as HTMLInputElement | null)?.value
        .trim()
        .toUpperCase();
      const title = (document.getElementById('ph-title') as HTMLInputElement | null)?.value.trim();
      if (!code || !title) {
        toast(t('actionPlans.error.code_title_required'), 'error');
        return;
      }
      const predecessor_ids = Array.from(
        document.querySelectorAll<HTMLInputElement>('.ph-pred-chk:checked'),
      ).map((c) => parseInt(c.value, 10));
      const is_smart =
        (document.getElementById('ph-smart') as HTMLInputElement | null)?.checked || false;
      const selVal = (id: string): number | null => {
        const v = (document.getElementById(id) as HTMLSelectElement | null)?.value;
        return v ? parseInt(v, 10) : null;
      };
      const smart_program_id = is_smart ? selVal('ph-smart-program') : null;
      const smart_project_id = is_smart ? selVal('ph-smart-project') : null;
      const smart_structure_id = is_smart ? selVal('ph-smart-structure') : null;
      const smart_committee_id = is_smart ? selVal('ph-smart-committee') : null;
      if (
        is_smart &&
        !smartCriteriaValid({
          programId: smart_program_id,
          projectId: smart_project_id,
          structureId: smart_structure_id,
          committeeId: smart_committee_id,
        })
      ) {
        toast(t('actionPlans.phases.smart.error.atLeastOne'), 'error');
        return;
      }
      const payload = {
        code,
        title,
        description:
          (document.getElementById('ph-desc') as HTMLTextAreaElement | null)?.value.trim() || null,
        date_start: (document.getElementById('ph-start') as HTMLInputElement | null)?.value || null,
        date_end_target:
          (document.getElementById('ph-end') as HTMLInputElement | null)?.value || null,
        predecessor_ids,
        is_smart,
        smart_program_id,
        smart_project_id,
        smart_structure_id,
        smart_committee_id,
      };
      try {
        const saved = (
          isNew ? await api.createPhase(planId, payload) : await api.updatePhase(ph.id, payload)
        ) as PhaseT;
        closeModal();
        toast(
          isNew ? t('actionPlans.phases.toast.created') : t('actionPlans.phases.toast.updated'),
        );
        // Une création/édition de phase recharge le plan complet : la réponse du
        // PUT/POST ne porte pas les pastilles santé `health` (calculées seulement
        // par le GET détail) ni les actions recalculées des autres phases.
        if (isNew) _replacePhase(saved);
        await _refreshPlan();
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
  setTimeout(() => {
    document.getElementById('ph-code')?.addEventListener('input', (e: Event) => {
      const tgt = e.target as HTMLInputElement | null;
      if (tgt) tgt.value = tgt.value.toUpperCase();
    });
    // Affiche/masque le bloc des critères selon la case « Phase intelligente ».
    const smartChk = document.getElementById('ph-smart') as HTMLInputElement | null;
    const smartBox = document.getElementById('ph-smart-fields');
    const syncSmart = (): void => {
      if (smartBox) smartBox.style.display = smartChk?.checked ? '' : 'none';
    };
    smartChk?.addEventListener('change', syncSmart);
    syncSmart();
  }, 0);
}

/** Bloc « Phase intelligente » de la modale : case à cocher + 4 critères
 *  (Programme / Projet / Organisation / Comité, un seul de chaque). */
function _smartFieldsHtml(ph: PhaseT): string {
  const isSmart = !!ph.is_smart;
  const refOpt = (
    items: MinRef[],
    selectedId: number | null | undefined,
    labelFn: (r: MinRef) => string,
  ): string =>
    `<option value="">${esc(t('actionPlans.phases.smart.any'))}</option>` +
    items
      .map(
        (r) =>
          `<option value="${r.id}" ${selectedId === r.id ? 'selected' : ''}>${esc(labelFn(r))}</option>`,
      )
      .join('');
  const codeName = (r: MinRef): string =>
    `${r.code || ''} — ${r.name || r.label || ''}`.replace(/^ — | — $/g, '');
  const projOpts = _projects.map((p) => ({ id: p.id, code: p.code, name: p.name }) as MinRef);
  // Le flag « intelligente » n'est modifiable que si la phase n'a AUCUNE action
  // (liée ou résolue) : on ne change pas le mode d'une phase déjà peuplée.
  const locked = (ph.id || 0) > 0 && (ph.action_count || 0) > 0;
  const lockedHint = locked
    ? `<p style="font-size:11px;color:var(--warn);margin:4px 0 0">${esc(t('actionPlans.phases.smart.locked'))}</p>`
    : '';
  return `
    <div class="form-group" style="margin-top:12px;border-top:1px solid var(--border2);padding-top:12px">
      <label class="struct-parent-opt" style="font-weight:600">
        <input type="checkbox" id="ph-smart" ${isSmart ? 'checked' : ''} ${locked ? 'disabled' : ''}>
        <span>${esc(t('actionPlans.phases.field.smart'))}</span>
      </label>
      ${lockedHint}
      <p style="font-size:11px;color:var(--text3);margin:4px 0 8px">${esc(t('actionPlans.phases.smart.help'))}</p>
      <div id="ph-smart-fields">
        <div class="form-row">
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.phases.smart.program'))}</label>
            <select id="ph-smart-program" style="width:100%">${refOpt(_programs, ph.smart_program_id, codeName)}</select></div>
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.phases.smart.project'))}</label>
            <select id="ph-smart-project" style="width:100%">${refOpt(projOpts, ph.smart_project_id, codeName)}</select></div>
        </div>
        <div class="form-row" style="margin-top:8px">
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.phases.smart.structure'))}</label>
            <select id="ph-smart-structure" style="width:100%">${refOpt(_structures, ph.smart_structure_id, codeName)}</select></div>
          <div class="form-group" style="flex:1"><label>${esc(t('actionPlans.phases.smart.committee'))}</label>
            <select id="ph-smart-committee" style="width:100%">${refOpt(_committees, ph.smart_committee_id, codeName)}</select></div>
        </div>
      </div>
    </div>`;
}

function confirmDeletePhase(p: PhaseT): void {
  confirmDelete({
    titleKey: 'actionPlans.phases.delete.title',
    bodyKey: 'actionPlans.phases.delete.body',
    bodyParams: { label: `${p.code} — ${p.title}` },
    onDelete: () => api.deletePhase(p.id),
    onDeleted: () => {
      toast(t('actionPlans.phases.toast.deleted'));
      _phases = _phases.filter((x) => x.id !== p.id);
      // Retire la phase supprimée des prédécesseurs des autres (cohérence UI).
      for (const ph of _phases) {
        ph.predecessor_ids = (ph.predecessor_ids || []).filter((id) => id !== p.id);
      }
      renderPhasesTree();
    },
  });
}

// ── Actions d'une phase : lier / créer / éditer / délier ──────────────────────

async function _refreshPlan(): Promise<void> {
  // Recharge le plan pour récupérer les listes d'actions à jour des phases.
  if (!_plan) return;
  try {
    const fresh = (await api.getActionPlan(_plan.id)) as PlanDetail;
    _plan = fresh;
    _phases = fresh.phases || [];
    renderPhasesTree();
  } catch {
    /* best-effort */
  }
}

async function unlinkAction(phaseId: number, actionId: number): Promise<void> {
  try {
    await api.unlinkActionFromPhase(phaseId, actionId);
    toast(t('actionPlans.action.toast.unlinked'));
    await _refreshPlan();
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
  }
}

// Action telle que renvoyée par GET /actions — champs utiles au picker et à la
// copie (template). Sur-ensemble de PhaseAction.
interface PickableAction {
  id: number;
  text?: string | null;
  status_name?: string | null;
  status_id?: number | null;
  deadline?: string | null;
  approver_id?: number | null;
  performers?: number[];
  consulted?: number[];
  informed?: number[];
  priority?: number | null;
}

/** Picker générique d'actions existantes (principe de #revision). Charge la
 *  liste, filtre, et appelle `onPick(action)` à la sélection. Le caller décide
 *  de fermer la modale (lier immédiatement, ou ouvrir l'éditeur pour copier). */
async function _pickActionModal(
  title: string,
  excludeIds: Set<number>,
  onPick: (a: PickableAction) => void,
): Promise<void> {
  openModal(
    title,
    `<div id="lk-body"><div class="loading">${esc(t('common.loading'))}</div></div>`,
    async () => {
      /* pas de confirmation : la sélection déclenche l'action */
    },
    null,
  );
  let actions: PickableAction[];
  try {
    actions = (await api.getAllActions({ includeCompleted: false })) as PickableAction[];
  } catch (e: unknown) {
    const host = document.getElementById('lk-body');
    if (host)
      host.innerHTML = `<div class="error">${esc(e instanceof Error ? e.message : t('common.error'))}</div>`;
    return;
  }
  const eligible = actions.filter((a) => !excludeIds.has(a.id));
  const renderList = (filter: string): string => {
    const lower = filter.toLowerCase();
    const matched = eligible.filter(
      (a) => !lower || (a.text || '').toLowerCase().includes(lower) || String(a.id) === lower,
    );
    if (!matched.length)
      return `<div style="color:var(--text3);font-size:12px;font-style:italic;padding:10px">${esc(t('actionPlans.action.noResult'))}</div>`;
    return matched
      .map(
        (a) => `<button type="button" class="lk-pick menu-hover-bg3" data-aid="${a.id}"
          style="display:flex;gap:8px;width:100%;text-align:left;padding:7px 10px;border:none;border-bottom:0.5px solid var(--border);cursor:pointer;font-size:12px">
          <span style="font-family:var(--font-mono);color:var(--text3);min-width:40px">#${a.id}</span>
          <span style="flex:1">${esc(a.text || '—')}</span>
          <span style="color:var(--text3)">${esc(a.status_name || '')}</span>
        </button>`,
      )
      .join('');
  };
  const host = document.getElementById('lk-body');
  if (!host) return;
  host.innerHTML = `
    <input type="text" id="lk-search" placeholder="${esc(t('actionPlans.action.search'))}" autocomplete="off"
      style="width:100%;font-size:13px;padding:7px 10px;border:1px solid var(--border2);border-radius:var(--radius);margin-bottom:8px">
    <div id="lk-list" style="max-height:360px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius)">${renderList('')}</div>`;
  const listEl = document.getElementById('lk-list');
  document.getElementById('lk-search')?.addEventListener('input', (e: Event) => {
    if (listEl) listEl.innerHTML = renderList((e.target as HTMLInputElement).value || '');
  });
  listEl?.addEventListener('click', (e: Event) => {
    const btn = (e.target as HTMLElement | null)?.closest('.lk-pick');
    if (!(btn instanceof HTMLElement)) return;
    const aid = parseInt(btn.dataset['aid'] || '', 10);
    const picked = eligible.find((x) => x.id === aid);
    if (picked) onPick(picked);
  });
}

/** « ⊙ Lier » : rattache une action existante à la phase (lien immédiat). */
function showLinkActionModal(phaseId: number): void {
  const phase = _phases.find((p) => p.id === phaseId);
  const alreadyLinked = new Set(
    (phase?.actions || []).map((a) => a.id).filter((id): id is number => id != null),
  );
  void _pickActionModal(t('actionPlans.action.linkTitle'), alreadyLinked, (a) => {
    void (async () => {
      try {
        await api.linkActionToPhase(phaseId, a.id);
        closeModal();
        toast(t('actionPlans.action.toast.linked'));
        await _refreshPlan();
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    })();
  });
}

/** « ⊕ Copier » : crée une NOUVELLE action pré-remplie depuis une existante
 *  (même logique que #revision), puis la lie à la phase. */
function showCopyActionModal(phaseId: number): void {
  void _pickActionModal(t('actionPlans.action.copyTitle'), new Set<number>(), (src) => {
    closeModal();
    const template = {
      text: src.text || '',
      deadline: src.deadline || null,
      deadline_revised: null,
      approver_id: src.approver_id ?? null,
      performers: [...(src.performers || [])],
      consulted: [...(src.consulted || [])],
      informed: [...(src.informed || [])],
      priority: typeof src.priority === 'number' ? src.priority : 2,
      status_id: src.status_id ?? null,
      progress: 0,
    };
    const opts = {
      users: _users,
      statuses: _statuses,
      projects: _projects,
      onSaved: (saved: unknown) => {
        const id = (saved as { id?: number } | null)?.id;
        if (!id) return;
        void (async () => {
          try {
            await api.linkActionToPhase(phaseId, id);
            toast(t('actionPlans.action.toast.linked'));
          } catch {
            /* lien best-effort */
          }
          await _refreshPlan();
        })();
      },
    } as unknown as EditActionOpts;
    window.showEditActionModal(template, opts);
  });
}

/** Ouvre l'éditeur d'action standard en création, puis lie l'action créée. */
function openCreateActionForPhase(phaseId: number): void {
  const opts = {
    users: _users,
    statuses: _statuses,
    projects: _projects,
    onSaved: (saved: unknown) => {
      const id = (saved as { id?: number } | null)?.id;
      if (!id) return;
      void (async () => {
        try {
          await api.linkActionToPhase(phaseId, id);
          toast(t('actionPlans.action.toast.linked'));
        } catch {
          /* lien best-effort */
        }
        await _refreshPlan();
      })();
    },
  } as unknown as EditActionOpts;
  window.showEditActionModal({}, opts);
}

export {};
